/**
 * When a Save can be pressed, and what it says when it cannot (Ryan, SURF-4889).
 *
 * Every Save in the CMS follows one rule: grey and off, with a tooltip saying why, until the
 * required fields are filled and there is something to save; then the primary blue. It
 * replaces SURF-4887's white Save, which could be pressed while a required field was still
 * empty and only refused the save afterwards.
 *
 * The shared editors get it from useItemEditActions; a page that draws its own Save passes
 * its own state through `saveBlockedReason`, or, when it has a validator that names the
 * problem, that message, and spreads `saveButtonState(reason)` onto the action or Button.
 * shared/ui/Button draws the reason: a Button that is `disabled` with a `disabledReason`
 * stays focusable and shows the reason in a tooltip.
 */

/**
 * The three reasons, for a commit that is not called Save: `verb` is what it does ("purge",
 * "create the report"), and ends each sentence.
 */
export const blockedMessages = (verb = 'save') => ({
  /** The form's rules do not pass and nothing on it shows an error: fields still empty. */
  required: `Fill out the required fields (marked *) to ${verb}.`,
  /** The form's rules do not pass and a field shows its error. */
  errors: `Fix the fields with errors to ${verb}.`,
  /** An existing record with nothing changed. */
  unchanged: `No changes to ${verb} yet.`,
});

export const SAVE_BLOCKED = Object.freeze(blockedMessages());

/**
 * What a grey Save asks for on the two custom field tables (App User Custom Fields, Lead
 * Gen Event Custom Fields), whose columns carry no asterisk to point at.
 */
export const CUSTOM_FIELD_ROWS_REQUIRED =
  'Fill out the Title, Unique ID and Type on every row to save.';

/**
 * Why Save cannot be pressed yet, or '' when it can.
 *
 * A form whose rules do not pass comes first, changed or not, since filling it in is what
 * the person has to do either way: a fresh Add page is empty, not unchanged. When a field
 * is showing its error the thing to do is fix it, otherwise a required field is still
 * empty; a form validated on change only shows a field's error once the field has been
 * changed, so an untouched empty field is the second case. Then, with the rules passing,
 * nothing changed means nothing to save. `verb` words it for a commit that is not a save,
 * and `required` replaces the second sentence where the fields carry no asterisk to point
 * at (the custom field tables say which columns every row needs).
 */
export const saveBlockedReason = ({
  hasChanges = true,
  isValid = true,
  hasErrors = false,
  verb,
  required,
} = {}) => {
  const messages = {
    ...(verb ? blockedMessages(verb) : SAVE_BLOCKED),
    ...(required ? { required } : {}),
  };
  if (!isValid) return hasErrors ? messages.errors : messages.required;
  if (!hasChanges) return messages.unchanged;
  return '';
};

/**
 * A page validator's answer as a reason: its message, or '' when it passed. The directory
 * checks throw their message as a string and hand it back; anything that throws a real
 * Error hands that back instead, and a tooltip cannot draw an object.
 */
export const reasonText = (result) => {
  if (!result) return '';
  if (typeof result === 'string') return result;
  return result.message || String(result);
};

/**
 * The fields that draw a Save by the rule: always the primary, off with its reason until it
 * is ready. Spread into an action (`{ label, onClick, ...saveButtonState(reason) }`) or onto
 * a Button. `reason` is '' (or nothing) when Save is ready, and `disabledReason` is then ''
 * rather than missing, so the Button keeps the same element as the reason comes and goes
 * (shared/ui/Button's ReasonTooltip).
 */
export const saveButtonState = (reason) => ({
  variant: 'primary',
  disabled: Boolean(reason),
  disabledReason: reason || '',
});

/**
 * Re-run the form's validity without showing any error, so `formState.isValid` matches what
 * the fields hold. react-hook-form 7.43 recomputes it when a field it registered changes, but
 * not after `setValue` without `shouldValidate`, which is how several shared fields write
 * (an audience, a link, a date picked from a calendar): a Save stuck grey with every field
 * filled. `_updateValid(true)` is the call react-hook-form makes itself when a component
 * subscribes to isValid (useFormState). A form without it (a test's stand-in) is left alone.
 */
export const refreshValidity = (form) => {
  const update = form && form.control && form.control._updateValid;
  if (typeof update === 'function') update(true);
};

/**
 * Keeps a form's isValid live through programmatic writes: every `setValue` and `reset`
 * re-runs validity (refreshValidity). A field the user types into needs nothing, since
 * react-hook-form validates its own change events, and those carry an event `type`.
 *
 * The re-run waits for the writes in hand to finish and runs once for all of them. An
 * editor that loads by writing several values and then resetting (App User does) would
 * otherwise have a re-run started mid-burst land last, with a validity that predates the
 * final values. Returns the unsubscribe, which also drops a re-run still waiting. useCmsForm
 * calls it for every editor; a page on its own useForm can call it too.
 */
export const keepValidityLive = (form) => {
  if (!form || typeof form.watch !== 'function') return () => {};
  let waiting = null;
  const subscription = form.watch((_values, { type } = {}) => {
    if (type || waiting) return;
    waiting = setTimeout(() => {
      waiting = null;
      refreshValidity(form);
    }, 0);
  });
  return () => {
    if (waiting) clearTimeout(waiting);
    waiting = null;
    subscription?.unsubscribe?.();
  };
};
